/**
 * Parses a roadmap SVG (Figma export, elements ordered per step) into a base
 * layer (the road) plus per-step groups (extension line + blue block + text
 * glyphs + illustration + numbered dot).
 *
 * Convention of the export: the road paths come first, then each step's
 * elements in sequence starting with its extension <line>, in 1..11 order.
 * A new step therefore begins at every top-level <line>. Elements before the
 * first line (or when no lines exist at all) stay in the always-visible base,
 * so an unexpected export degrades to a static image instead of disappearing.
 */

export interface IRoadmapArt {
  viewBox: string;
  defs: string;
  base: string;
  steps: string[];
}

/** Tokenize the flat list of top-level elements (handles nested containers). */
function topLevelElements(markup: string) {
  const elements: string[] = [];
  const tagRe = /<(\/?)([a-zA-Z][\w:-]*)((?:"[^"]*"|'[^']*'|[^"'>])*?)(\/?)>/g;
  let depth = 0;
  let start = -1;
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(markup))) {
    const [full, closing, , , selfClosing] = m;
    if (closing) {
      depth--;
      if (depth === 0 && start >= 0) {
        elements.push(markup.slice(start, m.index + full.length));
        start = -1;
      }
    } else if (selfClosing) {
      if (depth === 0) elements.push(full);
    } else {
      if (depth === 0) start = m.index;
      depth++;
    }
  }
  return elements;
}

export function parseRoadmap(svg: string): IRoadmapArt | null {
  const viewBox = svg.match(/viewBox="([^"]+)"/)?.[1];
  if (!viewBox) return null;

  const defs = svg.match(/<defs>[\s\S]*<\/defs>/)?.[0] ?? "";

  // Content = everything inside the root clip-path group, before <defs>.
  // Fall back to the full svg body for exports without a root clip group.
  const body =
    svg.match(/<g clip-path="[^"]*">([\s\S]*?)<\/g>\s*<defs>/)?.[1] ??
    svg.match(/<svg[^>]*>([\s\S]*?)(?:<defs>|<\/svg>)/)?.[1];
  if (!body) return null;

  const base: string[] = [];
  const steps: string[][] = [];
  let current: string[] | null = null;

  for (const el of topLevelElements(body)) {
    if (el.startsWith("<line")) {
      current = [el];
      steps.push(current);
    } else if (current) {
      current.push(el);
    } else {
      base.push(el);
    }
  }

  return {
    viewBox,
    defs,
    base: base.join("\n"),
    steps: steps.map((parts) => parts.join("\n")),
  };
}
